<script lang="ts">
	import type { RetrievedDocument } from 'chatbridge-client/src'
	import { Icon } from '@repo/commons/components'

	interface Props {
		doc: RetrievedDocument
		pages?: number[] // Array of page indices for PDF grouping
		onDocumentClick?: ((doc: RetrievedDocument) => void) | null
	}

	let { doc, pages = [], onDocumentClick = null }: Props = $props()

	const metadata = $derived(doc.knowledgeResourceMetadata)
	const isPDF = $derived(metadata?.type === 'PDF')
	const isFAQ = $derived(metadata?.type === 'FAQ')
	const isImage = $derived(metadata?.type === 'IMAGE')
	// Container is clickable only for images or PDFs without multiple pages
	const isClickable = $derived(!!onDocumentClick && (isImage || (isPDF && pages.length === 0)))
	// Note: chatbridge-client generated TypeScript uses camelCase for properties
	const pageIndex = $derived(metadata?.pageIndex ?? 0)
	const docInfo = $derived(doc.info || doc.content || '')

	// Create a modified document for page-specific clicks
	function createDocForPage(pageIdx: number): RetrievedDocument {
		return {
			...doc,
			knowledgeResourceMetadata: {
				...metadata,
				pageIndex: pageIdx
			}
		}
	}

	function handleKeyDown(e: KeyboardEvent) {
		if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
			e.preventDefault()
			onDocumentClick?.(doc)
		}
	}
</script>

<div
	class="document-reference"
	class:clickable={isClickable}
	onclick={() => (isClickable ? onDocumentClick?.(doc) : null)}
	onkeydown={handleKeyDown}
	role="button"
	tabindex={isClickable ? 0 : undefined}
>
	<div class="document-icon">
		{#if isPDF}
			<Icon name="pdf" class="text-gray-500" size="md" />
		{:else if isImage}
			<Icon name="image" class="text-gray-500" size="md" />
		{:else if isFAQ}
			<Icon name="questionMark" class="text-gray-500" size="md" />
		{:else}
			📄
		{/if}
	</div>

	<div class="document-content">
		{#if isPDF}
			<div class="document-details">
				<span class="document-name">{metadata?.name || 'Unknown'}</span>
				{#if pages.length > 0}
					<span class="document-page"
						>-
						{#each pages as page, idx}
							<button
								class="page-link"
								onclick={(e) => {
									e.stopPropagation()
									onDocumentClick?.(createDocForPage(page))
								}}
							>
								Page {page + 1}
							</button>{#if idx < pages.length - 1},{'\u00A0\u00A0'}{/if}
						{/each}
					</span>
				{:else}
					<span class="document-page">- Page {pageIndex + 1}</span>
				{/if}
			</div>
		{:else if isImage}
			<div class="document-details">
				<span class="document-name">Image Reference</span>
			</div>
		{:else if isFAQ}
			<div class="document-details">
				<span class="document-name">{metadata?.name || 'Unknown'}</span>
				{#if docInfo}
					<div class="document-question">Question: <b>{docInfo}</b></div>
				{/if}
			</div>
		{:else}
			<span class="document-name">{metadata?.name || 'Unknown Document'}</span>
		{/if}
	</div>
</div>

<style>
	.document-reference {
		@apply flex p-2 items-center gap-2 self-stretch rounded-lg bg-white;
	}

	.document-reference.clickable {
		@apply cursor-pointer transition-colors;
	}

	.document-reference.clickable:hover {
		background: rgba(0, 122, 204, 0.05);
	}

	.document-icon {
		@apply shrink-0;
	}

	.document-content {
		@apply flex-1 min-w-0;
	}

	.document-details {
		@apply flex items-center gap-1 flex-wrap text-sm;
	}

	.document-name {
		@apply text-sm font-normal leading-5 text-[#001626e6];
	}

	.document-reference.clickable .document-name {
		color: rgba(0, 22, 38, 0.9);
	}

	.document-reference.clickable:not(:has(.page-link)) .document-name {
		@apply underline cursor-pointer text-[#007ACC];
	}

	.document-page {
		@apply text-sm font-normal text-[#00162699];
	}

	.page-link {
		@apply text-sm font-normal leading-5 underline cursor-pointer bg-transparent border-none p-0 transition-colors text-[#007ACC];
	}

	.page-link:hover {
		color: #005a9e;
	}

	.document-question {
		@apply text-sm w-full mt-1 text-[#001626b3];
	}
</style>
